import { FC, useMemo } from 'react';

import { Selector } from 'components/ui/Selector/Selector';
import dayjs from 'dayjs';
import i18n from 'providers/Locale';
import Translation from 'types/Translation';

import { CustomSelectOption, CustomSelectProps } from '../../../../ui/Select';

import styles from './styles.module.scss';

export type WideSelectOption = Omit<CustomSelectOption, 'labelJSX'> & {
	labelJSX?: {
		left?: string | JSX.Element;
		right?: string | JSX.Element;
	};
};

type WideSelectProps = Omit<CustomSelectProps, 'options'> & {
	options?: WideSelectOption[];
	withDayOfWeek?: boolean;
	position?: 'top' | 'bottom' | 'right' | 'left';
};

const WideSelect: FC<WideSelectProps> = ({
	options,
	withDayOfWeek,
	position,
	value,
	name,
	error,
	onChange,
	label,
}) => {
	const optionList = useMemo(() => {
		return (options || []).map((option) => ({
			...option,
			labelInHead: option.labelInHead || option.label,
			labelJSX: <Item option={option} withDayOfWeek={withDayOfWeek} />,
		}));
	}, [options]);

	const selectedDayOfWeek = useMemo(() => {
		if (!withDayOfWeek) return null;
		const day = dayjs(value).format('dddd').toLowerCase();
		return i18n(`day.full.${day}` as keyof Translation);
	}, [value, withDayOfWeek]);

	return (
		<div className={styles.wideSelect}>
			<Selector.Dropdown
				{...(selectedDayOfWeek && { subLabel: selectedDayOfWeek })}
				label={label}
				name={name}
				value={value}
				error={error}
				onChange={onChange}
				options={optionList}
				position={position}
			/>
		</div>
	);
};

type ItemProps = {
	option: WideSelectOption;
	withDayOfWeek?: boolean;
};

const Item: React.FC<ItemProps> = ({ option, withDayOfWeek }) => {
	const dayOfWeek = useMemo(() => {
		if (!withDayOfWeek) return null;
		return dayjs(option.value).format('dddd').toLowerCase();
	}, [option.value, withDayOfWeek]);

	return (
		<div className={styles.wideSelectLabel} key={option.value}>
			<div className={styles.wideSelectLabelLeft}>{option.labelJSX?.left || option.label}</div>
			{dayOfWeek && (
				<div className={styles.weekDay}>{i18n(`day.ddd.${dayOfWeek}` as keyof Translation)}</div>
			)}
			{option.labelJSX?.right && (
				<div className={styles.wideSelectLabelRight}>{option.labelJSX.right}</div>
			)}
		</div>
	);
};

export default WideSelect;
